import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
import { StepVersionDataSchema } from './publish.schema.js';
extendZodWithOpenApi(z);
const uuidSchema = z.string().uuid('Invalid UUID format');
// ─── Input schemas ──────────────────────────────────────────────────────────
export const SubmitForReviewSchema = z.object({}).openapi('SubmitForReview');
export const ApproveVersionSchema = z
    .object({
    notes: z.string().max(1000).optional(),
})
    .openapi('ApproveVersion');
export const RequestChangesSchema = z
    .object({
    notes: z.string().min(1, 'Review notes are required').max(2000),
})
    .openapi('RequestChanges');
// ─── Review queue schemas ───────────────────────────────────────────────────
export const ReviewQueueQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(1),
    pageSize: z.coerce.number().int().min(1).max(100).default(20),
    sortBy: z.enum(['submittedAt', 'courseTitle']).default('submittedAt'),
    sortOrder: z.enum(['asc', 'desc']).default('desc'),
});
export const ReviewQueueItemSchema = z
    .object({
    courseVersionId: uuidSchema,
    courseId: uuidSchema,
    courseTitle: z.string(),
    versionNumber: z.number(),
    status: z.literal('in_review'),
    submittedBy: z.object({
        id: uuidSchema,
        /** Canonical display name from BetterAuth `user.name`. */
        name: z.string().nullable(),
        email: z.string().nullable().optional(),
    }),
    submittedAt: z.string(),
})
    .openapi('ReviewQueueItem');
// ─── Draft change summary schemas ───────────────────────────────────────────
export const StructuralChangeItemSchema = z.object({
    id: uuidSchema,
    title: z.string(),
});
export const StructuralChangeWithParentSchema = z.object({
    id: uuidSchema,
    title: z.string(),
    parentName: z.string(),
});
export const ReorderedItemSchema = z.object({
    type: z.enum(['module', 'topic', 'lesson']),
    id: uuidSchema,
    title: z.string(),
    oldPosition: z.number(),
    newPosition: z.number(),
});
export const MetadataChangeSchema = z.object({
    type: z.enum(['module', 'topic']),
    id: uuidSchema,
    title: z.string(),
    field: z.string(),
    oldValue: z.string().nullable(),
    newValue: z.string().nullable(),
});
export const StepChangeSchema = z.object({
    stepId: uuidSchema,
    changeType: z.enum(['modified', 'added', 'removed']),
    draftVersion: StepVersionDataSchema.nullable(),
    liveVersion: StepVersionDataSchema.nullable(),
});
export const LessonContentChangeSchema = z.object({
    lessonId: uuidSchema,
    lessonTitle: z.string(),
    metadataDiff: z
        .object({
        title: z
            .object({ old: z.string().nullable(), new: z.string().nullable() })
            .optional(),
        description: z
            .object({ old: z.string().nullable(), new: z.string().nullable() })
            .optional(),
        passPct: z
            .object({ old: z.number().nullable(), new: z.number().nullable() })
            .optional(),
    })
        .nullable(),
    stepChanges: z.array(StepChangeSchema),
});
export const DraftChangeSummarySchema = z
    .object({
    structuralChanges: z.object({
        addedModules: z.array(StructuralChangeItemSchema),
        removedModules: z.array(StructuralChangeItemSchema),
        addedTopics: z.array(StructuralChangeWithParentSchema),
        removedTopics: z.array(StructuralChangeWithParentSchema),
        addedLessons: z.array(StructuralChangeWithParentSchema),
        removedLessons: z.array(StructuralChangeWithParentSchema),
        reorderedItems: z.array(ReorderedItemSchema),
        metadataChanges: z.array(MetadataChangeSchema),
    }),
    contentChanges: z.array(LessonContentChangeSchema),
    mergeInfo: z.array(z.object({
        lessonId: uuidSchema,
        lessonTitle: z.string(),
    })),
})
    .openapi('DraftChangeSummary');
//# sourceMappingURL=review.schema.js.map